import { supabase } from "./supabase";
import { ENV } from "./env";
/**
* Cliente HTTP centralizado que habla con el backend FastAPI (Railway).
*
* Responsabilidades:
* - Inyecta `Authorization: Bearer <JWT>` desde la sesión Supabase actual
* - Maneja errores 401 disparando un sign-out (cualquier listener en el
* auth store se entera y redirige a /login)
* - Parsea JSON y normaliza errores FastAPI ({ detail: ... }) a top-level
* `error` y `code` para que el frontend lea un shape consistente
* - Timeout configurable por llamada (default 30s, uploads 120s)
*
* No hace retry automático — lo dejamos a TanStack Query (retry: 1).
*/
const DEFAULT_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 120_000;
export class ApiError extends Error {
status: number;
code?: string;
data?: unknown;
constructor(message: string, status: number, opts: { code?: string; data?: unknown } = {}) {
super(message);
this.name = "ApiError";
this.status = status;
this.code = opts.code;
this.data = opts.data;
}
}
async function getAuthHeader(): Promise<Record<string, string>> {
const { data } = await supabase.auth.getSession();
const token = data.session?.access_token;
return token ? { Authorization: `Bearer ${token}` } : {};
}
function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
const controller = new AbortController();
const timer = setTimeout(() => controller.abort(), timeoutMs);
return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}
function normalizeError(data: unknown, status: number): ApiError {
// FastAPI suele venir con { detail: "..." } o { detail: { error, code } }
if (data && typeof data === "object") {
const d = data as Record<string, unknown>;
if (typeof d.error === "string") {
return new ApiError(d.error, status, {
code: typeof d.code === "string" ? d.code : undefined,
data: d,
});
}
if (typeof d.detail === "string") {
return new ApiError(d.detail, status, { data: d });
}
if (d.detail && typeof d.detail === "object") {
const det = d.detail as Record<string, unknown>;
const msg = typeof det.error === "string" ? det.error : `Error ${status}`;
return new ApiError(msg, status, {
code: typeof det.code === "string" ? det.code : undefined,
data: d,
});
}
}
return new ApiError(`Error ${status}`, status, { data });
}
async function handle401() {
// Sesión expirada / JWT inválido. Sign-out limpio para forzar relogin.
await supabase.auth.signOut().catch(() => {});
}

/**
 * Salud del backend. Cuando un fetch falla por red (api caída, DNS, CORS
 * total) o por timeout, lo reportamos aquí para que el banner global avise.
 * Una respuesta exitosa lo vuelve a marcar como online.
 */
type ApiHealth = "online" | "offline";
let apiHealth: ApiHealth = "online";
const healthListeners = new Set<() => void>();

export function subscribeApiHealth(cb: () => void): () => void {
  healthListeners.add(cb);
  return () => healthListeners.delete(cb);
}

export function getApiHealth(): ApiHealth {
  return apiHealth;
}

function setApiHealth(next: ApiHealth) {
  if (apiHealth === next) return;
  apiHealth = next;
  healthListeners.forEach((cb) => cb());
}
interface RequestOptions {
method?: string;
body?: unknown;
headers?: Record<string, string>;
timeoutMs?: number;
/** Si true, no inyecta auth header (para endpoints públicos como /v1/demo). */
skipAuth?: boolean;
}
async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
const {
method = "GET",
body,
headers = {},
timeoutMs = DEFAULT_TIMEOUT_MS,
skipAuth = false,
} = opts;
const authHeader = skipAuth ? {} : await getAuthHeader();
const isFormData = body instanceof FormData;
const finalHeaders: Record<string, string> = {
Accept: "application/json",
...authHeader,
...headers,
};
if (!isFormData && body !== undefined) {
finalHeaders["Content-Type"] = "application/json";
}
const init: RequestInit = { method, headers: finalHeaders };
if (body !== undefined) {
init.body = isFormData ? body : JSON.stringify(body);
}
const url = `${ENV.API_BASE_URL}${path}`;
let res: Response;
try {
res = await fetchWithTimeout(url, init, timeoutMs);
} catch (err) {
// fetch lanza TypeError en fallo de red/CORS total, y AbortError en
// timeout. En ambos casos el backend es inalcanzable → offline.
setApiHealth("offline");
const isTimeout = err instanceof DOMException && err.name === "AbortError";
throw new ApiError(
isTimeout
? "El servidor tardó demasiado en responder."
: "No pudimos conectar con el servidor.",
0,
{ code: "network", data: { cause: String(err) } },
);
}
// Llegó respuesta (aunque sea error HTTP) → el backend está vivo.
setApiHealth("online");
const raw = await res.text();
let data: unknown = null;
if (raw) {
try {
data = JSON.parse(raw);
} catch {
data = { error: raw };
}
}
if (!res.ok) {
if (res.status === 401) {
void handle401();
}
throw normalizeError(data, res.status);
}
return data as T;
}
export const api = {
get: <T = unknown>(path: string, opts?: RequestOptions) =>
request<T>(path, { ...opts, method: "GET" }),
post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
request<T>(path, { ...opts, method: "POST", body }),
patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
request<T>(path, { ...opts, method: "PATCH", body }),
put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
request<T>(path, { ...opts, method: "PUT", body }),
delete: <T = unknown>(path: string, opts?: RequestOptions) =>
request<T>(path, { ...opts, method: "DELETE" }),
upload: <T = unknown>(path: string, formData: FormData, opts?: RequestOptions) =>
request<T>(path, {
...opts,
method: "POST",
body: formData,
timeoutMs: opts?.timeoutMs ?? UPLOAD_TIMEOUT_MS,
}),
};
